import { useEffect, useState, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { LeadFilters } from "../components/leads/LeadFilters";
import { LeadsTable } from "../components/leads/LeadsTable";
import { CreateLeadModal } from "../components/leads/CreateLeadModal";

import { LeadImport } from "../components/leads/LeadImport";
import { BulkEditModal } from "../components/leads/BulkEditModal";
import { BulkFilterModal } from "../components/leads/BulkFilterModal";
import { Plus, Search, Upload, Download, Edit3, AlertTriangle, UserPlus, UserMinus, ChevronDown, Shuffle } from "lucide-react";
import { toast } from "sonner";
import { deleteLead, fetchAvailableLeads, fetchAvailableLeadIds } from "../services/crmServices/leadService";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger
} from "../components/ui/dropdown-menu";
import {
    setAllLeads,
    setFilteredLeads,
    setCurrentFilters,
    setSearchTerm,
    setLoading,
    clearFilters,
    selectAllLeads,
    selectFilteredLeads,
    selectCurrentFilters,
    selectSearchTerm,
    selectLeadsLoading,
    deleteLead as deleteLeadAction,
    updateLead as updateLeadAction
} from "../store/slices/leadsSlice";
import { selectLeadStatuses } from "../store/slices/leadStatusSlice";
import { useAuth } from "../hooks/useAuth";
import { Checkbox } from "../components/ui/checkbox";
import { DuplicateCheckModal } from "../components/leads/DuplicateCheckModal";
import { BulkAssignModal } from "../components/leads/BulkAssignModal";
import { BulkUnassignModal } from "../components/leads/BulkUnassignModal";
import { ShuffleLeadsModal } from "../components/leads/ShuffleLeadsModal";
import { ExportLeadsModal } from "../components/leads/ExportLeadsModal";

import { format } from "date-fns";

export function Leads() {
    const { user } = useAuth();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // Redux state
    const allLeads = useSelector(selectAllLeads);
    const filteredLeads = useSelector(selectFilteredLeads);
    const currentFilters = useSelector(selectCurrentFilters);
    const searchTerm = useSelector(selectSearchTerm);
    const loading = useSelector(selectLeadsLoading);
    const leadStatuses = useSelector(selectLeadStatuses);

    // Local state
    const [showCreateModal, setShowCreateModal] = useState(false);

    const [showImportModal, setShowImportModal] = useState(false);
    const [editLead, setEditLead] = useState(null);
    const [showBulkEditModal, setShowBulkEditModal] = useState(false);
    const [showBulkFilterModal, setShowBulkFilterModal] = useState(false);
    const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
    const [showBulkUnassignModal, setShowBulkUnassignModal] = useState(false);
    const [showShuffleModal, setShowShuffleModal] = useState(false);
    const [showExportModal, setShowExportModal] = useState(false);
    const [selectedLeads, setSelectedLeads] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [currentPageLeads, setCurrentPageLeads] = useState([]);
    const [selectNCount, setSelectNCount] = useState("");
    const [showDuplicateModal, setShowDuplicateModal] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [pagination, setPagination] = useState(null);
    // Server-side sort config (applies to all pages)
    // Default: show most recently modified leads first (updatedAt DESC)
    const [sortConfig, setSortConfig] = useState({
        key: "updatedAt",
        direction: "desc"
    });
    const needsRefreshAfterModalCloseRef = useRef(false);
    // Track leads that were updated but no longer match filters - keep them visible
    const updatedLeadsCacheRef = useRef(new Map()); // Map<leadId, leadData>

    // Debounce search and filters, then reset to page 1
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, currentFilters]);



    // Update selectAll state when filtered leads change
    useEffect(() => {
        if (filteredLeads.length === 0) {
            setSelectAll(false);
        } else {
            const allSelected = filteredLeads.every((lead) =>
                selectedLeads.some((selected) => selected._id === lead._id)
            );
            setSelectAll(allSelected);
        }
    }, [filteredLeads, selectedLeads]);

    // Clear selected leads when filtered leads becomes empty
    useEffect(() => {
        if (filteredLeads.length === 0 && selectedLeads.length > 0) {
            setSelectedLeads([]);
        }
    }, [filteredLeads.length]);

    const fetchLeadsFromServer = useCallback(async () => {
        dispatch(setLoading(true));
        try {
            // Combine filters and search term
            const filters = {
                ...currentFilters,
                ...(searchTerm.trim() && { searchTerm: searchTerm.trim() })
            };
            
            // Remove undefined and empty values
            Object.keys(filters).forEach(key => {
                if (filters[key] === undefined || 
                    filters[key] === null ||
                    (Array.isArray(filters[key]) && filters[key].length === 0) ||
                    (typeof filters[key] === 'string' && filters[key].trim() === '')) {
                    delete filters[key];
                }
            });

            const response = await fetchAvailableLeads(currentPage, pageSize, filters, sortConfig);
            
            if (response?.data) {
                // Merge server response with existing Redux state to preserve updated leads
                // This ensures that leads updated in Redux aren't overwritten by stale server data
                const currentReduxLeads = allLeads || [];
                const serverLeads = response.data || [];
                
                // Create a map of current Redux leads by ID for quick lookup
                const reduxLeadsMap = new Map();
                currentReduxLeads.forEach(lead => {
                    if (lead?._id) {
                        reduxLeadsMap.set(lead._id.toString(), lead);
                    }
                });
                
                // Merge: prioritize Redux version if it exists (preserves user's updates)
                // Also include cached leads that were filtered out but should stay visible
                const mergedLeads = serverLeads.map(serverLead => {
                    const reduxLead = reduxLeadsMap.get(serverLead._id?.toString());
                    if (reduxLead) {
                        // Always prefer Redux version if it exists - it has user's latest updates
                        // Compare updatedAt timestamps to ensure we're using the most recent
                        const reduxUpdated = new Date(reduxLead.updatedAt || reduxLead.updated_at || 0);
                        const serverUpdated = new Date(serverLead.updatedAt || serverLead.updated_at || 0);
                        // Use Redux version if it's newer or equal (preserves user's updates)
                        return reduxUpdated >= serverUpdated ? reduxLead : serverLead;
                    }
                    return serverLead;
                });
                
                // Note: With server-side pagination, we rely on the server's response
                // Cached leads are not added as they would break pagination
                // If a lead is updated and no longer matches filters, it will disappear from view
                // which is the expected behavior
                
                // Don't add leads from other pages when using server-side pagination
                // The server controls what leads to show on each page
                // Only the cached leads (lines 165-177) are added if they were recently updated
                
                dispatch(setAllLeads(mergedLeads));
                dispatch(setFilteredLeads(mergedLeads));
                setPagination(response.pagination || null);
            } else {
                dispatch(setAllLeads([]));
                dispatch(setFilteredLeads([]));
                setPagination(null);
            }
        } catch (error) {
            console.error("Error fetching leads:", error);
            const errorMessage = error?.response?.data?.message || error?.message || "Failed to fetch leads";
            console.error("Error details:", {
                status: error?.response?.status,
                data: error?.response?.data,
                message: errorMessage
            });
            toast.error(errorMessage);
            dispatch(setAllLeads([]));
            dispatch(setFilteredLeads([]));
            setPagination(null);
        } finally {
            dispatch(setLoading(false));
        }
    }, [currentPage, pageSize, searchTerm, currentFilters, sortConfig, dispatch]);

        // Fetch leads when page, pageSize, searchTerm, or filters change
        useEffect(() => {
            fetchLeadsFromServer();
        }, [currentPage, pageSize, searchTerm, currentFilters, sortConfig]);

    const initLeads = async () => {
        // Reset to page 1 and fetch
        setCurrentPage(1);
        await fetchLeadsFromServer();
    };

    const refreshCurrentPage = useCallback(async () => {
        // Refresh current page without resetting to page 1
        await fetchLeadsFromServer();
    }, [fetchLeadsFromServer]);

    // Handle sort change from table (server-side sorting across all pages)
    // 3 states per column: DESC -> ASC -> none (fall back to default: updatedAt DESC)
    const handleSortChange = useCallback((key) => {
        setSortConfig((current) => {
            // New column or no current sort: start with DESC (latest first)
            if (!current || current.key !== key) {
                return { key, direction: "desc" };
            }

            // Toggle between DESC and ASC
            if (current.direction === "desc") {
                return { key, direction: "asc" };
            }

            // Third click: clear explicit sort and fall back to backend default (updatedAt DESC)
            return null;
        });
        setCurrentPage(1);
    }, []);

    // Helper function to check if a lead matches current filters
    const leadMatchesFilters = useCallback((lead) => {
        const filters = currentFilters;
        const search = searchTerm.trim().toLowerCase();

        // Search term filter
        if (search) {
            const searchFields = [
                lead.firstName,
                lead.lastName,
                lead.email,
                lead.phone,
                lead.secondPhone,
                lead.source,
                lead.affiliate,
                lead.country
            ].filter(Boolean).join(" ").toLowerCase();
            
            if (!searchFields.includes(search)) {
                return false;
            }
        }

        // Status filter
        if (filters.statuses && Array.isArray(filters.statuses) && filters.statuses.length > 0) {
            if (!filters.statuses.includes(lead.status)) {
                return false;
            }
        }

        // Desk filter with unassigned handling
        if (filters.desk && Array.isArray(filters.desk) && filters.desk.length > 0) {
            const hasUnassigned = filters.desk.includes("unassigned");
            const deskIds = filters.desk.filter(id => id !== "unassigned");
            
            if (hasUnassigned && deskIds.length > 0) {
                // Show unassigned OR specific desks
                const isUnassigned = !lead.desk;
                const isInSelectedDesks = lead.desk && deskIds.includes(lead.desk._id || lead.desk);
                if (!isUnassigned && !isInSelectedDesks) {
                    return false;
                }
            } else if (hasUnassigned) {
                // Only unassigned
                if (lead.desk) {
                    return false;
                }
            } else {
                // Only specific desks
                if (!lead.desk || !deskIds.includes(lead.desk._id || lead.desk)) {
                    return false;
                }
            }
        }

        // Agent filter
        if (filters.agent && Array.isArray(filters.agent) && filters.agent.length > 0) {
            if (!lead.agent || !filters.agent.includes(lead.agent._id || lead.agent)) {
                return false;
            }
        }

        // Country filter
        if (filters.country && Array.isArray(filters.country) && filters.country.length > 0) {
            if (!lead.country || !filters.country.includes(lead.country)) {
                return false;
            }
        }

        // Source filter
        if (filters.source && Array.isArray(filters.source) && filters.source.length > 0) {
            if (!lead.source || !filters.source.includes(lead.source)) {
                return false;
            }
        }

        // Affiliate filter
        if (filters.affiliate && Array.isArray(filters.affiliate) && filters.affiliate.length > 0) {
            if (!lead.affiliate || !filters.affiliate.includes(lead.affiliate)) {
                return false;
            }
        }

        return true;
    }, [currentFilters, searchTerm]);

    // Update lead in Redux and handle filter matching dynamically
    const handleLeadUpdate = useCallback(
        (updatedLead) => {
            if (!updatedLead || !updatedLead._id) {
                return;
            }

            // ALWAYS update lead in Redux for immediate UI feedback (NO PAGE REFRESH)
            dispatch(updateLeadAction(updatedLead));

            // Check if lead still matches current filters
            let leadFilteredOut = false;

            // For server-side pagination, check if lead still matches filters
            if (pagination && currentFilters.statuses && currentFilters.statuses.length > 0) {
                // Check if updated status matches current filter
                if (!currentFilters.statuses.includes(updatedLead.status)) {
                    leadFilteredOut = true;
                }
            } else {
                // For client-side pagination or no status filter, check if lead still matches all filters
                const stillMatches = leadMatchesFilters(updatedLead);
                if (!stillMatches) {
                    leadFilteredOut = true;
                }
            }

            // If lead was filtered out, cache it so it stays visible in current session
            // This prevents pagination from shifting when leads are updated
            if (leadFilteredOut) {
                updatedLeadsCacheRef.current.set(updatedLead._id.toString(), updatedLead);
                needsRefreshAfterModalCloseRef.current = true;
            } else {
                // If it still matches, remove from cache if it was there
                updatedLeadsCacheRef.current.delete(updatedLead._id.toString());
            }
            // If it still matches, the Redux update above is enough – no extra fetch needed.
        },
        [dispatch, pagination, currentFilters, leadMatchesFilters, fetchLeadsFromServer]
    );

    const handleSearch = (term) => {
        dispatch(setSearchTerm(term));
        setCurrentPage(1); // Reset to page 1 when search changes
    };

    const handleFilter = (filters) => {
        dispatch(setCurrentFilters(filters));
        setCurrentPage(1); // Reset to page 1 when filters change
        toast.success(`Applied filters`);
    };

    const handleClearFilters = () => {
        dispatch(clearFilters());
        setCurrentPage(1); // Reset to page 1 when filters cleared
        toast.info("Filters cleared");
    };

    const handleEdit = (lead) => {
        navigate(`/leads/edit/${lead._id}`);
    };

    const handleDelete = (leadId) => {
        Swal.fire({
            title: "Are you sure?",
            text: "You will not be able to recover this lead!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, delete it!",
            cancelButtonText: "No, keep it"
        }).then((result) => {
            if (result.isConfirmed) {
                // Show progress toast
                const progressToast = toast.loading("Deleting lead...");
                
                deleteLead({ _id: leadId }).then((res) => {
                    toast.dismiss(progressToast);
                    if (res.status !== 200) {
                        toast.error("Something went wrong while deleting the lead.");
                        return;
                    } else {
                        toast.success("Lead deleted successfully");
                        dispatch(deleteLeadAction(leadId));
                    }
                }).catch((error) => {
                    toast.dismiss(progressToast);
                    toast.error("Failed to delete lead");
                    console.error("Delete error:", error);
                });
            }
        });
    };

    const handleView = (lead) => {
        navigate(`/leads/view/${lead._id}`);
    };

    const handleApplyBulkFilters = (filters) => {
        // Apply the bulk filters to get filtered leads
        let filtered = [...allLeads];

        if (filters.statuses.length > 0) {
            filtered = filtered.filter((lead) => filters.statuses.includes(lead.status));
        }
        if (filters.desks.length > 0) {
            filtered = filtered.filter((lead) => filters.desks.includes(lead.desk?._id));
        }
        if (filters.agents.length > 0) {
            filtered = filtered.filter((lead) => filters.agents.includes(lead.agent?._id));
        }
        if (filters.affiliates.length > 0) {
            filtered = filtered.filter((lead) => filters.affiliates.includes(lead.affiliate));
        }
        if (filters.sources.length > 0) {
            filtered = filtered.filter((lead) => filters.sources.includes(lead.source));
        }
        if (filters.countries.length > 0) {
            filtered = filtered.filter((lead) => filters.countries.includes(lead.country));
        }
        if (filters.dateFrom) {
            filtered = filtered.filter((lead) => new Date(lead.createdAt) >= filters.dateFrom);
        }
        if (filters.dateTo) {
            filtered = filtered.filter((lead) => new Date(lead.createdAt) <= filters.dateTo);
        }

        // Update the filtered leads and close the modal
        dispatch(setFilteredLeads(filtered));
        setShowBulkFilterModal(false);

        // Clear any existing selections
        setSelectedLeads([]);
        setSelectAll(false);
    };

    const handleBulkEdit = () => {
        if (selectedLeads.length === 0) {
            toast.error("Please select leads to edit");
            return;
        }
        setShowBulkEditModal(true);
    };

    const handleBulkEditSuccess = () => {
        setShowBulkEditModal(false);
        setSelectedLeads([]);
        setSelectAll(false);
        initLeads();
    };

    const handleBulkAssign = () => {
        if (selectedLeads.length === 0) {
            toast.error("Please select leads to assign");
            return;
        }
        setShowBulkAssignModal(true);
    };

    const handleBulkAssignSuccess = () => {
        setShowBulkAssignModal(false);
        setSelectedLeads([]);
        setSelectAll(false);
        initLeads();
    };

    const handleBulkUnassign = () => {
        if (selectedLeads.length === 0) {
            toast.error("Please select leads to unassign");
            return;
        }
        setShowBulkUnassignModal(true);
    };

    const handleBulkUnassignSuccess = () => {
        setShowBulkUnassignModal(false);
        setSelectedLeads([]);
        setSelectAll(false);
        initLeads();
    };

    const handleShuffle = () => {
        if (selectedLeads.length === 0) {
            toast.error("Please select leads to shuffle");
            return;
        }
        setShowShuffleModal(true);
    };

    const handleShuffleSuccess = () => {
        setShowShuffleModal(false);
        setSelectedLeads([]);
        setSelectAll(false);
        initLeads();
    };

    const handleSelectAll = async (checked) => {
        if (!checked) {
            setSelectAll(false);
            setSelectedLeads([]);
            return;
        }

        // Fetch all lead IDs matching current filters
        setSelectAll(true);
        dispatch(setLoading(true));
        try {
            // Combine filters and search term
            const filters = {
                ...currentFilters,
                ...(searchTerm.trim() && { searchTerm: searchTerm.trim() })
            };
            
            // Remove undefined and empty values
            Object.keys(filters).forEach(key => {
                if (filters[key] === undefined || 
                    filters[key] === null ||
                    (Array.isArray(filters[key]) && filters[key].length === 0) ||
                    (typeof filters[key] === 'string' && filters[key].trim() === '')) {
                    delete filters[key];
                }
            });

            const response = await fetchAvailableLeadIds(filters);
            
            if (response?.data && Array.isArray(response.data)) {
                // Create minimal lead objects with just _id for selected leads
                const allLeadIds = response.data;
                const minimalLeads = allLeadIds.map(id => ({ _id: id }));
                setSelectedLeads(minimalLeads);
                toast.success(`Selected all ${allLeadIds.length.toLocaleString()} leads matching filters`);
            } else {
                toast.error("Failed to fetch all lead IDs");
                setSelectAll(false);
            }
        } catch (error) {
            console.error("Error fetching all lead IDs:", error);
            toast.error("Failed to select all leads");
            setSelectAll(false);
        } finally {
            dispatch(setLoading(false));
        }
    };

    const handleSelectFirstN = async () => {
        const count = parseInt(selectNCount, 10);
        
        if (!count || count <= 0) {
            toast.error("Please enter a valid number greater than 0");
            return;
        }

        // Fetch all lead IDs matching current filters
        dispatch(setLoading(true));
        try {
            // Combine filters and search term
            const filters = {
                ...currentFilters,
                ...(searchTerm.trim() && { searchTerm: searchTerm.trim() })
            };
            
            // Remove undefined and empty values
            Object.keys(filters).forEach(key => {
                if (filters[key] === undefined || 
                    filters[key] === null ||
                    (Array.isArray(filters[key]) && filters[key].length === 0) ||
                    (typeof filters[key] === 'string' && filters[key].trim() === '')) {
                    delete filters[key];
                }
            });

            const response = await fetchAvailableLeadIds(filters);
            
            if (response?.data && Array.isArray(response.data)) {
                const allLeadIds = response.data;
                const totalFiltered = allLeadIds.length;
                
                if (count > totalFiltered) {
                    toast.warning(`Only ${totalFiltered.toLocaleString()} leads match filters. Selecting all ${totalFiltered.toLocaleString()} leads.`);
                }
                
                // Take only the first N lead IDs
                const firstNIds = allLeadIds.slice(0, count);
                const minimalLeads = firstNIds.map(id => ({ _id: id }));
                
                // Replace existing selections with the first N leads
                setSelectedLeads(minimalLeads);
                setSelectAll(false);
                
                const selectedCount = Math.min(count, totalFiltered);
                toast.success(`Selected first ${selectedCount.toLocaleString()} of ${totalFiltered.toLocaleString()} filtered leads`);
            } else {
                toast.error("Failed to fetch lead IDs");
            }
        } catch (error) {
            console.error("Error fetching lead IDs:", error);
            toast.error("Failed to select leads");
        } finally {
            dispatch(setLoading(false));
        }
    };

    const handleSelectLead = (lead, checked) => {
        if (checked) {
            setSelectedLeads((prev) => [...prev, lead]);
        } else {
            setSelectedLeads((prev) => prev.filter((selected) => selected._id !== lead._id));
        }
    };

    const handleSelectCurrentPageLeads = () => {
        if (currentPageLeads.length === 0) {
            toast.warning("No leads on current page to select");
            return;
        }
        
        // Replace existing selections with current page leads
        setSelectedLeads([...currentPageLeads]);
        setSelectAll(false);
        
        toast.success(`Selected ${currentPageLeads.length} lead${currentPageLeads.length !== 1 ? 's' : ''} from current page`);
    };

    const isLeadSelected = (leadId) => {
        return selectedLeads.some((lead) => lead._id === leadId);
    };

    const handleCreateSuccess = (leadData) => {
        if (editLead) {
            const updatedLeads = allLeads.map((lead) => (lead._id === leadData._id ? leadData : lead));
            dispatch(setAllLeads(updatedLeads));
            setEditLead(null);
        } else {
            const newLeads = [leadData, ...allLeads];
            dispatch(setAllLeads(newLeads));
        }
    };

    const handleImportSuccess = () => {
        // Refresh leads after successful import
        initLeads();
    };

    const handleCloseModal = () => {
        setShowCreateModal(false);
        setEditLead(null);
    };

    const handleDuplicateCheckSuccess = () => {
        setShowDuplicateModal(false);
        initLeads(); // Refresh leads to show updated duplicate flags
    };

    const handleExportToExcel = () => {
        if (!selectedLeads || selectedLeads.length === 0) {
            toast.error("Please select leads to export");
            return;
        }
        setShowExportModal(true);
    };

    return (
        <div className="w-full max-w-full px-4 sm:px-6 py-4">
            <div className="mb-4">
                <h1 className="text-xl font-bold text-gray-900 mb-1">Leads Management</h1>

                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-3">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-3 flex-1 w-full sm:w-auto">
                        <div className="relative w-full sm:max-w-md">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <Input
                                placeholder="Search by name, email, phone, desk..."
                                value={searchTerm}
                                onChange={(e) => handleSearch(e.target.value)}
                                className="pl-10 h-9 text-sm"
                            />
                        </div>
                        <LeadFilters onFilter={handleFilter} onClear={handleClearFilters} allLeads={allLeads} />
                    </div>

                    <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                        {/admin|generalManager|deskManager|floorManager/.test(user.position.role) && (
                            <>
                                {/* Bulk Actions Dropdown */}
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button
                                            variant="outline"
                                            className="flex-1 sm:flex-none"
                                            disabled={selectedLeads.length === 0}
                                        >
                                            <Edit3 className="h-4 w-4 mr-2 text-gray-500" />
                                            Bulk Actions ({selectedLeads.length})
                                            <ChevronDown className="h-3.5 w-3.5 ml-2 text-gray-400" />
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-56">
                                        <DropdownMenuItem
                                            onClick={handleBulkEdit}
                                            disabled={selectedLeads.length === 0}
                                            className="cursor-pointer"
                                        >
                                            <Edit3 className="h-4 w-4 mr-2" />
                                            Bulk Edit
                                        </DropdownMenuItem>
                                        <DropdownMenuItem
                                            onClick={handleBulkAssign}
                                            disabled={selectedLeads.length === 0}
                                            className="cursor-pointer"
                                        >
                                            <UserPlus className="h-4 w-4 mr-2" />
                                            Bulk Assign
                                        </DropdownMenuItem>
                                        <DropdownMenuItem
                                            onClick={handleBulkUnassign}
                                            disabled={selectedLeads.length === 0}
                                            className="cursor-pointer"
                                        >
                                            <UserMinus className="h-4 w-4 mr-2" />
                                            Bulk Unassign
                                        </DropdownMenuItem>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem
                                            onClick={handleShuffle}
                                            disabled={selectedLeads.length === 0}
                                            className="cursor-pointer"
                                        >
                                            <Shuffle className="h-4 w-4 mr-2" />
                                            Shuffle Leads
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </>
                        )}

                        {/* Add Duplicate Check Button for Admin Only */}
                        {user.position.role === "admin" && (
                            <Button
                                onClick={() => setShowDuplicateModal(true)}
                                variant="outline"
                                className="flex-1 sm:flex-none border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100"
                            >
                                <AlertTriangle className="h-4 w-4 mr-2" />
                                Duplicate Check
                            </Button>
                        )}

                        {user.position.role === "admin" && (
                            <Button
                                onClick={() => setShowImportModal(true)}
                                variant="outline"
                                className="flex-1 sm:flex-none border-indigo-300 bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
                            >
                                <Upload className="h-4 w-4 mr-2" />
                                Import
                            </Button>
                        )}
                        {user.permissions.leads.create && (
                            <Button
                                onClick={() => navigate("/leads/create")}
                                className="flex-1 sm:flex-none"
                            >
                                <Plus className="h-4 w-4 mr-2" />
                                Add Lead
                            </Button>
                        )}
                        {user.position.role === "admin" && (
                            <Button
                                onClick={handleExportToExcel}
                                variant="outline"
                                className="flex-1 sm:flex-none border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                            >
                                <Download className="h-4 w-4 mr-2" />
                                Export
                            </Button>
                        )}
                    </div>
                </div>
            </div>

            {/* Bulk Selection Info */}
            {user.position.role !== "agent" && filteredLeads.length > 0 && (
                <div className="mb-4 p-3 bg-white rounded-lg border border-gray-200 shadow-sm">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <div className="flex items-center space-x-2">
                                <Checkbox checked={selectAll} onCheckedChange={handleSelectAll} />
                                <span className="text-sm font-medium text-gray-700">
                                    Select All ({pagination?.totalCount ? pagination.totalCount.toLocaleString() : filteredLeads.length} leads)
                                </span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <Input
                                    type="number"
                                    placeholder="Count"
                                    value={selectNCount}
                                    onChange={(e) => setSelectNCount(e.target.value)}
                                    className="w-24 h-8 text-sm"
                                    min="1"
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                            handleSelectFirstN();
                                        }
                                    }}
                                />
                                <Button
                                    onClick={handleSelectFirstN}
                                    variant="outline"
                                    size="sm"
                                    className=""
                                    disabled={!selectNCount || parseInt(selectNCount, 10) <= 0}
                                >
                                    Select First N
                                </Button>
                                <Button
                                    onClick={handleSelectCurrentPageLeads}
                                    variant="outline"
                                    size="sm"
                                    className=""
                                    disabled={currentPageLeads.length === 0}
                                >
                                    Select Page
                                </Button>
                            </div>
                            {selectedLeads.length > 0 && (
                                <span className="text-sm text-indigo-600 font-semibold">
                                    {selectedLeads.length} selected
                                </span>
                            )}
                        </div>
                        {selectedLeads.length > 0 && (
                            <Button
                                onClick={() => {
                                    setSelectedLeads([]);
                                    setSelectAll(false);
                                }}
                                variant="outline"
                                size="sm"
                                className="text-gray-500 hover:text-gray-700"
                            >
                                Clear Selection
                            </Button>
                        )}
                    </div>
                </div>
            )}

            <LeadsTable
                leads={filteredLeads}
                selectedLeads={selectedLeads}
                onSelectLead={handleSelectLead}
                isLeadSelected={isLeadSelected}
                showBulkSelection={user.position.role !== "agent"}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onView={handleView}
                loading={loading}
                initLeads={initLeads}
                refreshCurrentPage={refreshCurrentPage}
                onLeadUpdate={handleLeadUpdate}
                onModalClose={() => {
                    // Refresh if needed (when lead was filtered out during save)
                    if (needsRefreshAfterModalCloseRef.current) {
                        needsRefreshAfterModalCloseRef.current = false;
                        // Clear cache when modal closes - leads will be filtered out on next fetch
                        updatedLeadsCacheRef.current.clear();
                        fetchLeadsFromServer();
                    }
                }}
                showStatusBadges={true}
                onCurrentPageLeadsChange={setCurrentPageLeads}
                serverSidePagination={true}
                pagination={pagination}
                currentPage={currentPage}
                onPageChange={setCurrentPage}
                pageSize={pageSize}
                onPageSizeChange={setPageSize}
                currentFilters={currentFilters}
                onFilterChange={handleFilter}
                useServerSort={true}
                serverSortConfig={sortConfig}
                onServerSortChange={handleSortChange}
            />

            <CreateLeadModal
                open={showCreateModal}
                onClose={handleCloseModal}
                onSuccess={handleCreateSuccess}
                editLead={editLead}
            />

            {showImportModal && (
                <LeadImport onClose={() => setShowImportModal(false)} onSuccess={handleImportSuccess} />
            )}

            <BulkFilterModal
                open={showBulkFilterModal}
                onClose={() => setShowBulkFilterModal(false)}
                onApplyFilters={handleApplyBulkFilters}
                allLeads={allLeads}
            />

            <BulkEditModal
                open={showBulkEditModal}
                onClose={() => setShowBulkEditModal(false)}
                selectedLeads={selectedLeads}
                onSuccess={handleBulkEditSuccess}
            />

            <DuplicateCheckModal
                open={showDuplicateModal}
                onClose={() => {
                    setShowDuplicateModal(false);
                    initLeads();
                }}
                onSuccess={handleDuplicateCheckSuccess}
            />

            <BulkAssignModal
                open={showBulkAssignModal}
                onClose={() => setShowBulkAssignModal(false)}
                selectedLeads={selectedLeads}
                onSuccess={handleBulkAssignSuccess}
            />

            <BulkUnassignModal
                open={showBulkUnassignModal}
                onClose={() => setShowBulkUnassignModal(false)}
                selectedLeads={selectedLeads}
                onSuccess={handleBulkUnassignSuccess}
            />

            <ShuffleLeadsModal
                open={showShuffleModal}
                onClose={() => setShowShuffleModal(false)}
                onSuccess={handleShuffleSuccess}
                selectedLeads={selectedLeads}
                allLeads={allLeads}
            />

            <ExportLeadsModal
                open={showExportModal}
                onClose={() => setShowExportModal(false)}
                selectedLeads={selectedLeads}
                leadStatuses={leadStatuses}
            />
        </div>
    );
}
